import { createLogic } from 'redux-logic';
import { stateGo } from 'redux-ui-router';
import { normalize, schema } from 'normalizr';
import store from 'store';

import {
  findKey,
  get,
  includes,
  isEmpty,
  kebabCase,
  mapKeys,
  mapValues,
  pick,
  pickBy,
} from 'lodash';

import { authLogout } from './auth';
import { goRedirect } from 'logic/redirect';
import {
  CHARGIFY_ERROR_CODES,
  SIGNUP_REDIRECT_LIST,
  SIGNUP_TEXT,
} from '../constants';

// Actions
const FETCH = '@@app/signup/FETCH';
const FETCH_SUCCESS = '@@app/signup/FETCH_SUCCESS';
const FETCH_ERROR = '@@app/signup/FETCH_ERROR';

const SETUP = '@@app/signup/SETUP';
const SETUP_SUCCESS = '@@app/signup/SETUP_SUCCESS';
const SETUP_ERROR = '@@app/signup/SETUP_ERROR';

const VERIFY = '@@app/signup/VERIFY';
const VERIFY_CREDITCARD = '@@app/signup/VERIFY_CREDITCARD';
const VERIFY_SUCCESS = '@@app/signup/VERIFY_SUCCESS';
const VERIFY_ERROR = '@@app/signup/VERIFY_ERROR';

const STATUS = '@@app/signup/STATUS';
const STATUS_SUCCESS = '@@app/signup/STATUS_SUCCESS';
const STATUS_ERROR = '@@app/signup/STATUS_ERROR';

const RESTORE = '@@app/signup/RESTORE';
const UPDATE = '@@app/signup/UPDATE';
const UPDATE_DATA = '@@app/signup/UPDATE_DATA';
const TRANSACTION = '@@app/signup/TRANSACTION';
const RESET = '@@app/signup/RESET';

export const initialState = {
  isDisabled: false,
  isLoading: false,
  coupon: null,
  message: null,
  step: 1,
  type: 'weekly',
  voomeProducts: null,
  voomeProductId: null,
  chargifyData: {
    apiId: null,
    timestamp: null,
    nonce: null,
    data: null,
    signature: null,
    email: null,
    emailConfirmation: null,
    firstName: null,
    lastName: null,
    cardNumber: null,
    expirationMonth: null,
    expirationYear: null,
    initialChargeInCents: null,
    subscriptionId: null,
    redirectUri: null,
  },
};

// Observables
const chargifyStatusLogic = createLogic({
  type: STATUS,
  processOptions: {
    successType: STATUS_SUCCESS,
    failType: STATUS_ERROR,
  },
  process({ httpClient }) {
    const endpoint = '/v1/chargify-subscriptions/check-product';
    const params = { roundId: 1 };
    const onSuccess = response => ({
      isDisabled: isEmpty(response.data.product),
    });

    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const fetchProductsLogic = createLogic({
  type: FETCH,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: FETCH_ERROR,
  },
  process({ httpClient, action }) {
    const endpoint = '/v1/voome/products';
    const params = { coupon: action.payload.coupon };
    const onSuccess = response =>
      mapKeys(response.data, item => {
        item.coupon = params.coupon;
        return item.id;
      });

    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const resetLogic = createLogic({
  type: RESET,
  process({}, dispatch, done) {
    store.remove('signup');
    done();
  },
});

const setupLogic = createLogic({
  type: SETUP,
  processOptions: {
    successType: SETUP_SUCCESS,
    failType: SETUP_ERROR,
  },
  process({ action, httpClient }) {
    const endpoint = '/v1/voome/subscriptions/setup';
    let params = action.payload;
    params.voomeProductId = 33; //Coaching Zone product discount 100%
    const onSuccess = response => {
      if (response.data.product) {
        const { totalInCents } = response.data.product;

        if (totalInCents) {
          store.set('signup', { ...store.get('signup'), totalInCents });
        }
      }

      return response.data;
    };

    store.set('signup', params);

    return httpClient.post(endpoint, params).then(onSuccess);
  },
});

const setupChargifyErrorLogic = createLogic({
  type: SETUP_ERROR,
  transform({ action }, next) {
    const { code, response } = action.payload;

    const prepend = code
      ? CHARGIFY_ERROR_CODES[code]
      : response.data && response.data.errors
        ? response.data.errors[0]
        : 'There was an error setting up';

    const message =
      prepend === 'You have already purchased this product'
        ? 'You already have a Coaching Zone account. <a href="/" target="_self">Click here</a> to login / reactivate your account'
        : `${prepend}. Please try again.`;

    next({
      ...action,
      payload: {
        ...action.payload,
        message,
      },
    });
  },
});

const submitTransactionLogic = createLogic({
  type: TRANSACTION,
  process({ action, dataLayer }, dispatch, done) {
    const data = store.get('signup');
    const { memberId, subscription } = action.payload;

    if (memberId && subscription && data) {
      data.memberId = memberId;
      data.subscriptionId = subscription.id;
      data.subscriptionTitle = subscription.title;
      dataLayer.track('transaction', getTransactionData(data));
    }

    dispatch(reset());
    done();
  },
});

const verifyLogic = createLogic({
  type: VERIFY,
  processOptions: {
    successType: VERIFY_SUCCESS,
    failType: VERIFY_ERROR,
  },
  process({ action, httpClient }) {
    const endpoint = '/v1/voome/subscriptions/verify';
    const params = action.payload;
    const onSuccess = response => response.data;

    return httpClient.post(endpoint, params).then(onSuccess);
  },
});

const verifyCreditCardLogic = createLogic({
  type: VERIFY_CREDITCARD,
  process({ action, httpClient }, dispatch, done) {
    const endpoint = '/v1/voome/subscriptions/verify-card-update';
    const { params, redirectTo } = action.payload;

    const onSuccess = response => {
      dispatch(reset());

      if (redirectTo.name) {
        redirectTo.name === 'dashboard'
          ? window.location.assign('/dashboard')
          : dispatch(stateGo(redirectTo.name, redirectTo.params));
      }
    };

    const onFail = () => dispatch({ type: VERIFY_ERROR });

    return httpClient
      .post(endpoint, params)
      .then(onSuccess)
      .catch(onFail)
      .then(done);
  },
});

const verifySuccessLogic = createLogic({
  type: VERIFY_SUCCESS,
  process({ action, dataLayer, memberStorage }, dispatch, done) {
    const { authToken, memberId } = action.payload;
    const { coupon, email, firstName, lastName, voomeProductId } = store.get(
      'signup'
    );

    dataLayer.track('paymentverified', {
      email,
      firstName,
      lastName,
      memberId,
      voomeProductId,
    });

    if (authToken) {
      memberStorage.set({ memberId, voomeProductId });
      window.location.assign('/welcome');
    } else if (includes(SIGNUP_REDIRECT_LIST, coupon)) {
      dispatch(goRedirect());
    } else {
      const redirectTo = { name: 'welcome' };
      const message = `Welcome back ${firstName}! As a returning Coaching Zone member, please login with your existing password. Forgot your password? Just click the 'forgot' password link below.`;
      memberStorage.set({ redirectTo, voomeProductId });
      dispatch(authLogout(message));
    }

    done();
  },
});

export const signupLogic = [
  chargifyStatusLogic,
  fetchProductsLogic,
  resetLogic,
  setupLogic,
  setupChargifyErrorLogic,
  submitTransactionLogic,
  verifyLogic,
  verifyCreditCardLogic,
  verifySuccessLogic,
];

// Selectors
export const selectProducts = state => {
  return state.voomeProducts;
};

export const selectProduct = (state, voomeProductId) => {
  const products = selectProducts(state) || {};
  const product = products[voomeProductId];

  return product
    ? {
        ...product,
        products: product.products.map(id => products[id]),
      }
    : undefined;
};

export const selectProductBySlug = (state, slug) => {
  const key = findKey(
    selectProducts(state),
    item => kebabCase(item.name) === slug
  );

  return selectProduct(state, key);
};

export const selectProductsByType = (state, type = 'weekly') => {
  return pickBy(selectProducts(state), { type });
};

export const selectUpgradeProduct = (state, voomeProductId) => {
  const products = selectProducts(state);
  const key = findKey(
    products,
    item => item.products.indexOf(voomeProductId) >= 0
  );

  return key ? products[key] : {};
};

// Helpers
export const getTransactionData = signup => {
  const {
    memberId,
    voomeProductId,
    subscriptionTitle,
    subscriptionId,
    email,
    totalInCents,
  } = signup;

  const chargeInCents = parseFloat(totalInCents, 10);
  const taxInCents = Math.round(chargeInCents - chargeInCents / 1.1);
  const transactionTotal = (chargeInCents / 100).toFixed(2);
  const transactionTax = (taxInCents / 100).toFixed(2);
  const transactionTitle = kebabCase(subscriptionTitle);

  return {
    transactionId: subscriptionId,
    transactionMemberId: memberId,
    transactionEmail: email,
    transactionRegion: 'AU',
    transactionTitle,
    transactionTotal,
    transactionTax,
    transactionProducts: [
      {
        name: 'Voome Product',
        category: transactionTitle,
        method: 'CC',
        id: subscriptionId,
        price: transactionTotal,
        quantity: 1,
        sku: `VPROD-${transactionTitle.toUpperCase()}`,
      },
    ],
  };
};

export const parseSetupParams = (params = {}) =>
  pick(params, [
    'email',
    'firstName',
    'lastName',
    'programSignUp',
    'redirectUri',
    'subscriptionId',
  ]);

export const parseVerifyParams = (params = {}) =>
  pick(params, [
    'api_id',
    'call_id',
    'nonce',
    'result_code',
    'signature',
    'status_code',
    'timestamp',
    'voome_product_id',
  ]);

export const getWeeklyProductText = ({ type, name, products }) => {
  return products && products.length
    ? products.map(product => SIGNUP_TEXT.weekly[product.name])
    : type === 'weekly' ? [SIGNUP_TEXT.weekly[name]] : undefined;
};

// Action Creators
export const fetchProducts = coupon => {
  return { type: FETCH, payload: { coupon } };
};

export const checkStatus = () => {
  return { type: STATUS };
};

export const reset = () => {
  return { type: RESET };
};

export const restore = () => {
  return { type: RESTORE, payload: store.get('signup') || {} };
};

export const setup = params => {
  const {
    voomeProductId,
    chargifyData,
    coupon,
    utmParams,
    campaignChannelMedium,
  } = params;

  return {
    type: SETUP,
    payload: {
      ...parseSetupParams(chargifyData),
      campaignChannelMedium,
      coupon,
      utmParams,
      voomeProductId,
    },
  };
};

export const setupChargifyError = (params = {}) => {
  return { type: SETUP_ERROR, payload: { code: params.result_code || 4000 } };
};

export const submitTransactionData = (memberId, subscription) => {
  return { type: TRANSACTION, payload: { memberId, subscription } };
};

export const update = (key, value) => {
  return { type: UPDATE, payload: { key, value } };
};

export const updateChargifyData = (key, value) => {
  return { type: UPDATE_DATA, payload: { key, value } };
};

export const verify = payload => {
  return { type: VERIFY, payload: parseVerifyParams(payload) };
};

export const verifyCreditCard = (params, redirectTo) => {
  return {
    type: VERIFY_CREDITCARD,
    payload: {
      params: parseVerifyParams(params),
      redirectTo,
    },
  };
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case STATUS:
      return {
        ...state,
        isLoading: true,
      };

    case FETCH:
      return {
        ...state,
        isLoading: true,
        coupon: payload.coupon,
        message: '',
      };

    case SETUP:
      return {
        ...state,
        isLoading: true,
        message: '',
        chargifyData: {
          ...state.chargifyData,
          email: payload.email || null,
          firstName: payload.firstName || null,
          lastName: payload.lastName || null,
          voomeProductId: payload.voomeProductId || null,
          redirectUri: payload.redirectUri || null,
          subscriptionId: payload.subscriptionId || null,
        },
      };

    case RESTORE:
      return {
        ...state,
        voomeProductId: payload.voomeProductId,
        chargifyData: {
          ...state.chargifyData,
          email: payload.email || null,
          emailConfirmation: payload.email || null,
          firstName: payload.firstName || null,
          lastName: payload.lastName || null,
        },
      };

    case UPDATE:
      return {
        ...state,
        message: '',
        [payload.key]: payload.value,
      };

    case UPDATE_DATA:
      return {
        ...state,
        message: '',
        chargifyData: {
          ...state.chargifyData,
          [payload.key]: payload.value,
        },
      };

    case VERIFY:
    case VERIFY_CREDITCARD:
      return {
        ...state,
        isLoading: true,
        step: 3,
      };

    case RESET:
      return {
        ...initialState,
        voomeProducts: state.voomeProducts,
      };

    case STATUS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isDisabled: payload.isDisabled,
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        voomeProducts: {
          ...state.voomeProducts,
          ...payload,
        },
      };

    case SETUP_SUCCESS:
      const { apiId, data, nonce, signature, timestamp, product } = payload;

      return {
        ...state,
        isLoading: false,
        step: 2,
        chargifyData: {
          ...state.chargifyData,
          apiId,
          data,
          nonce,
          signature,
          timestamp,
        },
        voomeProducts: product
          ? {
              ...state.voomeProducts,
              [product.id]: {
                ...state.voomeProducts[product.id],
                discountInCents: product.discountInCents,
                totalInCents: product.totalInCents,
              },
            }
          : { ...state.voomeProducts },
      };

    case VERIFY_SUCCESS:
      return {
        ...state,
        isLoading: false,
        message: '',
      };

    case STATUS_ERROR:
      return {
        ...state,
        isLoading: false,
        isDisabled: true,
      };

    case SETUP_ERROR:
      return {
        ...state,
        isLoading: false,
        step: 1,
        message: payload.message,
      };

    case VERIFY_ERROR:
      return {
        ...state,
        isLoading: false,
        step: 1,
        message: `
          An error occurred verifying your transaction. Please <a href="/profile/credit-card/resume" target="_self">Click here</a> to try again and if
          the issues persists please contact support.`,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/signup.js